import React, { useState, useEffect } from 'react';
import {
  FaVideo, FaDownload, FaInfinity, FaCertificate, FaShareAlt,
  FaTwitter, FaLinkedin, FaChevronDown, FaChevronUp, FaLock
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import { decodeData, encodeData } from '../../utils/encodeAndEncode';
import { getPublicCourseDetails, AddMyCourses } from '../../services/userService';
import NoData from '../../components/common/NoData';
import { getLogoByDomainAndAlt } from '../../utils/logoUtils';

const courseImg = '/images/course/course1.png';
const DefaultProfile = '/images/profile/default-profile.png';

const getCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case 'INR': return '₹';
    case 'USD': return '$';
    case 'SGD': return 'S$';
    case 'EUR': return '€';
    case 'GBP': return '£';
    default: return '$';
  }
};

const domain = process.env.REACT_APP_DOMAIN_URL;

function CourseDetails() {
  const navigate = useNavigate(); 
  const [expandedModules, setExpandedModules] = useState(['module1']);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isPaidEnrolling, setIsPaidEnrolling] = useState(false);
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { encodedId } = useParams();
  const decoded = decodeData(encodedId);
  const courseId = decoded?.id;

  useEffect(() => {
    if (courseId) {
      getCourseDetails();
    } else {
      setError('Invalid course URL');
      setLoading(false);
    }
  }, [courseId]);

  const processCourseData = (data) => {
    if (!data) return data;
    
    const processed = { ...data };
    
    if (typeof processed.tags === 'string') {
      try {
        processed.tags = JSON.parse(processed.tags);
      } catch (error) {
        processed.tags = [];
      }
    }
    
    if (typeof processed.course_info === 'string') {
      try {
        processed.course_info = JSON.parse(processed.course_info);
      } catch (error) {
        processed.course_info = [];
      }
    }
    
    return processed;
  };

  async function getCourseDetails() {
    try {
      setLoading(true);
      const response = await getPublicCourseDetails({
        course_id: courseId,
        domain : window.location.origin
      });
      
      if (response.success) {
        const processedData = processCourseData(response.data);
        setCourseData(processedData);
      } else {
        setError('Course not available');
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setError('Course not available');
      } else if (error.response && error.response.data && error.response.data.message === 'Course not found or inactive') {
        setError('Course not available');
      } else {
        setError('Failed to load course');
      }
    } finally {
      setLoading(false);
    }
  }

  async function EnrollFreeCourse() {
    if (domain === 'lms.tpi.sg') {
      window.location.href = 'https://lms.tpi.sg/';
    } else if (domain === 'lms.nxgenvarsity.com') {
      window.location.href = 'https://lms.nxgenvarsity.com/';
    } else if (domain === 'lms.creatorfoundation.in') {
      window.location.href = 'https://lms.creatorfoundation.in/';
    } else {
      window.location.href = 'https://lms.tpi.sg/';
    }
  }
  
  const handlePaidEnrollment = async () => {
    if (domain === 'lms.tpi.sg') {
      window.location.href = 'https://lms.tpi.sg/';
    } else if (domain === 'lms.nxgenvarsity.com') {
      window.location.href = 'https://lms.nxgenvarsity.com/';
    } else if (domain === 'lms.creatorfoundation.in') {
      window.location.href = 'https://lms.creatorfoundation.in/';
    } else {
      window.location.href = 'https://lms.tpi.sg/';
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev =>
      prev.includes(moduleId)
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading course...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="text-center py-12">
          {error === 'Course not available' ? (
            <>
              <Icon icon="mdi:book-outline" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-500 mb-2">Course Not Available</h3>
              <p className="text-gray-500 mb-4">This course may have been deleted or is no longer accessible.</p>
              <button 
                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg inline-flex items-center gap-2"
                onClick={() => navigate('/')}
              >
                <Icon icon="mdi:home" className="w-5 h-5" />
                Go to Home
              </button>
            </>
          ) : (
            <>
              <Icon icon="mdi:alert-circle-outline" className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-500 mb-2">Error</h3>
              <p className="text-gray-500">{error}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!courseData) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="text-center py-12">
          <Icon icon="mdi:book-outline" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-500 mb-2">Course Not Found</h3>
          <p className="text-gray-500">The course you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const hasModules = courseData.modules && Object.keys(courseData.modules).length > 0;

  return (
    <div className="min-h-screen bg-light-background">
      {/* Logo Section */}
      <div className="max-w-7xl mx-auto py-4 px-4">
        <div className="text-center">
          <img 
            src={getLogoByDomainAndAlt().logo} 
            alt={getLogoByDomainAndAlt().alt}
            className="h-14 max-w-[200px] mx-auto object-contain"
          />
        </div>
      </div>
      
      {/* Course Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold mb-4 break-words">
            {courseData.course_name || "Untitled Course"}
          </h1>
          <p className="text-gray-300 mb-4">
            {courseData.tags && Array.isArray(courseData.tags) ? courseData.tags.join(' | ') : 'No tags available'}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Icon icon="material-symbols:star" className="text-yellow-400" />
              <span>{courseData?.courseMeta?.averageRating || '0.0'} rating</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon icon="material-symbols:menu-book" />
              <span>{courseData?.courseMeta?.modulesCount || 0} Modules</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon icon="material-symbols:update" />
              <span>Duration: {courseData?.courseMeta?.totalVideoDuration || '00:00:00'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon icon="material-symbols:school" />
              <span>Level: {courseData?.levels || 'Not specified'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon icon="material-symbols:language" />
              <span>Language: {courseData?.course_language || 'Not specified'}</span>
            </div>
          </div>
          
          {/* Instructor */}
          <div className="flex items-center gap-3 mt-4">
            <img
              src={courseData?.trainer?.profile || DefaultProfile}
              alt={courseData?.trainer?.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            <div>
              <div className="text-sm">By {courseData?.trainer?.name || 'Loading...'}</div>
              <div className="text-xs text-gray-400">{courseData?.trainer?.role || 'Instructor'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Course Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-xl font-bold text-font-primary mb-4">Course Info</h3>
              <div className="space-y-3">
                {courseData.course_info && Array.isArray(courseData.course_info) ? (
                  courseData.course_info.map((point, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <Icon icon="material-symbols:check-circle" className="text-green-500 w-5 h-5 flex-shrink-0" />
                      <span className="text-font-secondary">{point}</span>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center gap-3">
                    <Icon icon="material-symbols:check-circle" className="text-green-500 w-5 h-5" />
                    <span className="text-gray-500">Course information not available</span>
                  </div>
                )}
              </div>
            </div>

            {/* About Course */}
            {courseData.course_desc && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-xl font-bold text-font-primary mb-4">About This Course</h3>
                <p className="text-font-secondary break-words">{courseData.course_desc}</p>
              </div>
            )}

            {/* Course Content */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-xl font-bold text-font-primary mb-4">Course Content</h3>
              {hasModules ? (
                <div className="space-y-2">
                  {Object.entries(courseData.modules).map(([moduleName, contents]) => (
                    <div key={moduleName} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                        onClick={() => toggleModule(moduleName)}
                      >
                        <div className="flex items-center gap-2">
                          {expandedModules.includes(moduleName) ? <FaChevronDown /> : <FaChevronUp />}
                          <span className="font-medium">{moduleName}</span>
                        </div>
                        <span className="text-sm text-gray-500">{contents.length} items</span>
                      </button>
                      {expandedModules.includes(moduleName) && (
                        <div className="divide-y divide-gray-100">
                          {contents.map((content, idx) => (
                            <div key={idx} className="flex items-center justify-between p-4 hover:bg-gray-50">
                              <div className="flex items-center gap-3">
                                {content.type === 'Video' && <Icon icon="material-symbols:play-circle-outline" className="text-gray-500" />}
                                {content.type === 'Document' && <Icon icon="material-symbols:description-outline" className="text-gray-500" />}
                                {content.type === 'Assessment' && <Icon icon="material-symbols:quiz-outline" className="text-gray-500" />}
                                {content.type === 'Survey' && <Icon icon="material-symbols:analytics-outline" className="text-gray-500" />}
                                <span className="text-font-secondary">{content.title}</span>
                              </div>
                              <div className="text-sm text-gray-500">
                                {content.type === 'Video' && content.duration}
                                {content.type === 'Document' && content.fileType}
                                {content.type === 'Assessment' && content.questions}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <NoData caption="No course content available yet" />
              )}
            </div>

            {/* Instructor */}
            {courseData.trainer && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-xl font-bold text-font-primary mb-4">Instructor</h3>
                <div className="flex flex-col md:flex-row gap-4">
                  <img
                    src={courseData?.trainer?.profile || DefaultProfile}
                    alt={courseData?.trainer?.name}
                    className="w-32 h-32 rounded-xl object-cover hidden md:block"
                  />
                  <div>
                    <h4 className="text-lg font-semibold text-font-primary">{courseData?.trainer?.name}</h4>
                    <p className="text-gray-500 mb-3">{courseData?.trainer?.role || 'Instructor'}</p>
                    <p className="text-font-secondary">{courseData?.trainer?.bio || 'No bio available'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Course Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden sticky top-4">
              <img
                src={courseData.banner_image || courseImg}
                className="w-full h-48 object-cover"
                alt={courseData.course_name || "Course Preview"}
                onError={(e) => { e.target.src = courseImg; }}
              />
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-3xl font-bold text-font-primary">
                    {getCurrencySymbol(courseData?.currency)} {courseData?.course_type?.toLowerCase() === 'free' ? '0' : courseData?.course_price || '0'}
                  </span>
                  <span className="text-gray-400 line-through">
                    {getCurrencySymbol(courseData?.currency)}{courseData?.course_type?.toLowerCase() === 'free' ? '0' : Number(courseData?.course_price || 0) + 5}
                  </span>
                </div>

                {courseData?.course_type?.toLowerCase() === 'paid' ? (
                  <button
                    onClick={handlePaidEnrollment}
                    className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors mb-4"
                    disabled={isPaidEnrolling}
                  >
                    {isPaidEnrolling ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:lock" className="w-5 h-5" />
                        Enroll Now
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={EnrollFreeCourse}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors mb-4"
                    disabled={isEnrolling}
                  >
                    {isEnrolling ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        Enrolling...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:play-circle" className="w-5 h-5" />
                        {courseData.is_course_purchase ? 'Continue Learning' : 'Enroll Now'}
                      </>
                    )}
                  </button>
                )}

                <div className="flex items-center justify-center gap-2 text-gray-500 text-sm mb-6">
                  <FaInfinity />
                  Full lifetime access
                </div>

                <div>
                  <h4 className="font-semibold text-font-primary mb-3">This course includes:</h4>
                  {hasModules ? (
                    <ul className="space-y-2 text-sm text-font-secondary">
                      <li className="flex items-center gap-2">
                        <Icon icon="material-symbols:menu-book" className="text-gray-400" />
                        {courseData?.courseMeta?.modulesCount || 0} Modules
                      </li>
                      <li className="flex items-center gap-2">
                        <FaVideo className="text-gray-400" />
                        {courseData?.modules && Object.values(courseData.modules).flat().filter(item => item.type === 'Video').length || 0} Videos
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon icon="material-symbols:quiz" className="text-gray-400" />
                        {courseData?.modules && Object.values(courseData.modules).flat().filter(item => item.type === 'Assessment').length || 0} Assessments
                      </li>
                      <li className="flex items-center gap-2">
                        <Icon icon="material-symbols:description-outline" className="text-gray-400" />
                        {courseData?.modules && Object.values(courseData.modules).flat().filter(item => item.type === 'Document').length || 0} Documents
                      </li>
                      <li className="flex items-center gap-2">
                        <FaInfinity className="text-gray-400" />
                        Full lifetime access
                      </li>
                      <li className="flex items-center gap-2">
                        <FaCertificate className="text-gray-400" />
                        Certificate of completion
                      </li>
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-sm">Course content is being prepared</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseDetails;
